import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "@/lib/api";

/**
 * HeroTopBanner — full-viewport-width auto-scroll banner strip that sits ABOVE
 * the main hero. Cinematic 2.7:1 aspect landscape banners (~140-170px tall on
 * a 1440px viewport, taller on mobile due to letterboxing). Edge-to-edge, no
 * horizontal padding. Fetches admin-uploaded banners first
 * (/api/public/hero-top-banners → landing_banners where position="hero_top"),
 * falls back to the four bundled WebPs at /hero-top/01..04.webp?v=20260911a.
 *
 * Admin control: Admin → Content → Landing Banners → position "hero_top".
 */

const ASSET_V = "20261005h";
const DEFAULT_SLIDES = [1, 2, 3, 4].map((n) => ({
    id: `hero-top-${n}`,
    image_url: `/hero-top/${String(n).padStart(2, "0")}.webp?v=${ASSET_V}`,
    alt: `Family legacy banner ${n}`,
    link: null,
}));

const AUTO_MS = 5000;

export function HeroTopBanner() {
    const [slides, setSlides] = useState(DEFAULT_SLIDES);
    const [idx, setIdx] = useState(0);

    useEffect(() => {
        let cancel = false;
        api.get("/public/hero-top-banners")
            .then(({ data }) => {
                if (cancel) return;
                if (Array.isArray(data?.items) && data.items.length > 0) setSlides(data.items);
            })
            .catch(() => { /* keep defaults */ });
        return () => { cancel = true; };
    }, []);

    useEffect(() => {
        if (slides.length < 2) return undefined;
        const t = setInterval(() => setIdx((v) => (v + 1) % slides.length), AUTO_MS);
        return () => clearInterval(t);
    }, [slides.length]);

    const current = slides[idx] || slides[0];
    if (!current) return null;

    const Inner = (
        <AnimatePresence mode="wait">
            <motion.img
                key={current.id || idx}
                src={current.image_url}
                alt={current.alt || "Family legacy banner"}
                initial={{ opacity: 0, scale: 1.02 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.99 }}
                transition={{ duration: 0.6, ease: "easeInOut" }}
                loading="eager"
                className="absolute inset-0 h-full w-full object-cover object-left lg:object-center"
                data-testid={`hero-top-slide-${idx}`}
            />
        </AnimatePresence>
    );

    return (
        <section
            data-testid="hero-top-banner"
            aria-label="Family legacy highlight banner"
            className="relative w-full overflow-hidden bg-[#2a1a08] min-h-[150px]"
            style={{ aspectRatio: "1920 / 384" }}
        >
            {current.link ? (
                <a href={current.link} className="absolute inset-0 z-[1]" aria-label={current.alt || "Banner"} data-testid="hero-top-link">
                    {Inner}
                </a>
            ) : Inner}

            {/* Right-side scrim so the tagline stays legible over the imagery (desktop) */}
            <div className="hidden lg:block absolute inset-y-0 right-0 w-[42%] bg-gradient-to-l from-black/35 via-black/15 to-transparent pointer-events-none z-[2]" />

            {/* Tagline on the right — crisp HTML text (localisable), like the reference */}
            <div className="hidden lg:flex absolute inset-y-0 right-[4%] z-[3] flex-col items-end justify-center text-right pointer-events-none">
                <p className="font-serif leading-tight text-white drop-shadow-[0_2px_6px_rgba(0,0,0,0.55)]" style={{ fontSize: "clamp(18px, 1.9vw, 30px)" }}>
                    Your family.&nbsp; Your stories.&nbsp; Your legacy.
                </p>
                <span className="mt-3 block h-[2px] w-28 bg-gradient-to-r from-transparent via-[#F0C86A] to-transparent" />
            </div>

            {/* Dots — bottom-right, low-emphasis */}
            {slides.length > 1 && (
                <div className="absolute bottom-2 sm:bottom-3 right-3 sm:right-5 flex items-center gap-1.5 z-10" data-testid="hero-top-dots">
                    {slides.map((_, i) => (
                        <button
                            key={i}
                            type="button"
                            aria-label={`Show banner ${i + 1}`}
                            data-testid={`hero-top-dot-${i}`}
                            onClick={() => setIdx(i)}
                            className={`h-1.5 rounded-full transition-all ${
                                i === idx ? "w-5 bg-[#C9A55B]" : "w-1.5 bg-[#C9A55B]/50 hover:bg-[#C9A55B]/80"
                            }`}
                        />
                    ))}
                </div>
            )}
        </section>
    );
}

export default HeroTopBanner;
